
import { loadStripe } from '@stripe/stripe-js';

// Helper to safely access environment variables
const getEnv = (key: string, fallback: string): string => {
  try {
    // Check for import.meta.env (Vite) or process.env (Node/CommonJS environments)
    // @ts-ignore
    const viteEnv = (typeof import.meta !== 'undefined' && import.meta.env) ? import.meta.env[key] : undefined;
    // @ts-ignore
    const nodeEnv = (typeof process !== 'undefined' && process.env) ? process.env[key] : undefined;
    
    return viteEnv || nodeEnv || fallback;
  } catch (e) {
    return fallback;
  }
};

// Get Stripe publishable key from environment
// Use test key (pk_test_...) in development, live key (pk_live_...) in production
// IMPORTANT: Never use live keys in development - Stripe requires HTTPS for live keys
const getStripeKey = (): string => {
  const envKey = getEnv("VITE_STRIPE_PUBLISHABLE_KEY", "");
  
  if (envKey) {
    return envKey;
  }
  
  // Fallback: Use test key for development (safe for HTTP)
  // Replace with your test key from Stripe Dashboard
  const isDevelopment = import.meta.env.DEV || window.location.hostname === 'localhost';
  
  if (isDevelopment) {
    console.warn('[STRIPE] Using test key for development. Set VITE_STRIPE_PUBLISHABLE_KEY for production.');
    // TODO: Replace with your actual test key
    return 'pk_test_YOUR_TEST_KEY_HERE';
  }
  
  // Production: require environment variable
  throw new Error('VITE_STRIPE_PUBLISHABLE_KEY must be set in production. Live keys require HTTPS.');
};

const publishableKey = getStripeKey();
const stripePromise = loadStripe(publishableKey);

/**
 * Initiates the Stripe Checkout flow by calling the backend to create a session
 * and then redirecting the user to Stripe's hosted checkout page.
 * 
 * @param planId - The plan identifier ('free' | 'pro' | 'unlimited')
 * @param userId - The user's unique identifier
 * @param billingPeriod - The billing period ('monthly' | 'yearly'). Defaults to 'monthly' for backward compatibility.
 */
export const initiateCheckout = async (planId: string, userId: string, billingPeriod: 'monthly' | 'yearly' = 'monthly') => {
  try {
    const response = await fetch('/api/create-checkout-session', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ planId, userId, billingPeriod }),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Failed to create checkout session');
    }

    const { sessionId } = await response.json();
    const stripe = await stripePromise;

    if (!stripe) {
      throw new Error('Stripe failed to initialize');
    }

    const { error } = await stripe.redirectToCheckout({ sessionId });

    if (error) {
      console.error('Stripe redirection error:', error.message);
      alert('Error redirecting to checkout: ' + error.message);
    }
  } catch (err: unknown) {
    console.error('Checkout error:', err);
    const error = err as { message?: string };
    const errorMessage = error.message || 'Unknown error occurred';
    alert('Failed to start checkout process: ' + errorMessage);
    throw err; // Re-throw to allow caller to handle
  }
};
